import { FieldArray, FieldArrayRenderProps, Input, Toggle } from 'components/form';
import { YES_NO_OPTIONS } from 'constants/form';
import { useFormSubField } from 'hooks/form';
import isEqual from 'lodash/isEqual';
import { _SurgeryInput } from 'models/endpoint';
import React from 'react';
import { useTranslation } from 'react-i18next';
import AddItem from '../AddItem';
import RemoveItem from '../RemoveItem';

export type Surgery = _SurgeryInput;
export const DEFAULT_SURGERY: Surgery = {
  id: undefined,
  surgery: '',
  surgery_date: '',
  record_type: '',
  institution: ''
};

interface SurgeryItemProps {
  index: number;
  helpers: FieldArrayRenderProps;
  showAdd: boolean;
  showRemove: boolean;
  onChangeCb: (data?: any) => void;
}

const SurgeryItem: React.FC<SurgeryItemProps> = React.memo(({ index, helpers, showAdd, showRemove, onChangeCb }) => {
  const [t] = useTranslation('patient_profile');
  const { getSubName } = useFormSubField('surgeries');
  const insert = () => helpers.push(DEFAULT_SURGERY);
  const remove = () => {
    helpers.remove(index);
    onChangeCb();
  };
  return (
    <React.Fragment>
      <div className="flex-row">
        {index === 0 ? (
          <div className="flex-child">
            <Toggle name={getSubName('surgery', index)} items={YES_NO_OPTIONS} onChange={onChangeCb} fullWidth />
          </div>
        ) : null}
        <div className="flex-child" style={{ flexGrow: index === 0 ? 3 : 1 }}>
          <Input label={t('type')} name={getSubName('record_type', index)} fullWidth />
        </div>
      </div>
      <div className="flex-row">
        <div className="flex-child">
          <Input type="date" label={t('date')} name={getSubName('surgery_date', index)} fullWidth />
        </div>
        <div className="flex-child" style={{ flexGrow: 3 }}>
          <Input label={t('institution')} name={getSubName('institution', index)} fullWidth />
        </div>
      </div>
      <div className="add-multiple">
        {showAdd ? <AddItem onClick={insert} /> : null}
        {showRemove ? <RemoveItem onClick={remove} /> : null}
      </div>
    </React.Fragment>
  );
});

interface Props {
  list: Surgery[];
  onChangeCb: (data?: any) => void;
}

const Surgeries: React.FC<Props> = props => {
  const [t] = useTranslation('appointment');
  const { getName } = useFormSubField('surgeries');
  const { list, onChangeCb } = props;
  const totalItems = list.length;
  const lastItemIndex = totalItems - 1;
  return (
    <div className="section">
      <label className="sub-header grey">{t('surgery')}</label>
      <FieldArray
        name={getName()}
        render={helpers =>
          list.map((_, index: number) => (
            <SurgeryItem
              key={index}
              index={index}
              helpers={helpers}
              showAdd={totalItems === 1 || (totalItems > 1 && lastItemIndex === index)}
              showRemove={index > 0 && lastItemIndex === index}
              onChangeCb={onChangeCb}
            />
          ))
        }
      />
    </div>
  );
};

export default React.memo(Surgeries, (prev, next) => isEqual(prev.list, next.list));
